import React, { useState } from "react";
import { ISystemConfig, ISystemConfigSearch } from "../../vm";
import {
  getAllSystemConfigs,
  deleteSystemConfig,
} from "../../services/system-config/SystemConfigService";
import {
  IStackTokens,
  Stack,
  PrimaryButton,
  Fabric,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  TooltipHost,
  IDropdownOption,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  Callout,
  DirectionalHint,
  SearchBox,
  Dropdown,
} from "office-ui-fabric-react";
import { Loading } from "../../services/Loading";
import PageTitle from "../common/PageTitle";
import {
  // mdiPencilOutline,
  // mdiTrashCanOutline,
  // mdiDatabaseEdit,
  // mdiDatabaseSettings,
  mdiDotsVertical,
  mdiFilterOutline,
} from "@mdi/js";
import NoDataTile from "../common/NoDataTile";
import Icon from "@mdi/react";
import { THEME, BRAND_SECONDARY } from "../../utils/Styles";
import { ToastContext } from "../common/ToastProvider";
import { ConfirmDialogContext } from "../common/ConfirmDialogProvider";
import {
  SC_SYSTEM_TYPE_DIC,
  SC_CONNECTION_TYPE_DIC,
  childrenGap8,
  childrenGap,
  SC_SYSTEM_TYPE,
} from "../common/Constants";
import ManageSystemConfig from "./ManageSystemConfig";
// import ManageDatabaseDialog from "./ManageDatabaseDialog";
import ManageDatabaseDialogTable from "./ManageDatabaseDialogTable";
import { getAllDataCenters } from "../../services/datacenter/DatacenterService";
import { ReactComponent as SystemConfigIcon } from "../../images/system-configuration.svg";

export interface SystemConfigMainProps {}

const SystemConfigMain: React.FC<SystemConfigMainProps> = () => {
  //state
  const [state, setState] = React.useState({
    systemConfigs: [],
    isLoading: false,
    isDataLoaded: false,
    manageSystemConfigDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    manageDatabasesDialog: {
      isOpen: false,
      index: undefined,
      data: undefined,
    },
    datacenterDropdown: [],
    manageActionsCallout: {
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
    },
    showDcFilter: false,
  } as {
    systemConfigs: ISystemConfig[];
    isLoading: boolean;
    isDataLoaded: boolean;
    manageSystemConfigDialog: {
      isOpen: boolean;
      index?: number;
      data?: ISystemConfig;
    };
    manageDatabasesDialog: {
      isOpen: boolean;
      index?: number;
      data?: ISystemConfig;
    };
    datacenterDropdown: IDropdownOption[];
    manageActionsCallout: {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: ISystemConfig;
    };
    showDcFilter: boolean;
  });
  const [searchObj, setSearchObj] = useState({
    search: "",
    datacenterId: "all",
    systemType: "all",
  } as ISystemConfigSearch);

  //hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  const childrenGap24: IStackTokens = {
    childrenGap: 24,
  };

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getSystemConfigs();
    };
    asyncFunc();
  }, []);

  const getSystemConfigs = async (search?: ISystemConfigSearch) => {
    if (!search) setState({ ...state, isLoading: true });
    // search
    let updatedSearch = { ...searchObj };
    if (search) {
      updatedSearch = { ...updatedSearch, ...search };
    }
    if (updatedSearch.datacenterId === "all") delete updatedSearch.datacenterId;
    if (updatedSearch.systemType === "all") delete updatedSearch.systemType;
    let result = await getAllSystemConfigs(updatedSearch);
    if (result?.success) {
      // setState({
      //   ...state,
      //   systemConfigs: result.data,
      //   isDataLoaded: true,
      //   isLoading: false,
      // });

      if (search) {
        // setting up searchObj state
        setSearchObj({
          ...updatedSearch,
          datacenterId: updatedSearch.datacenterId || "all",
          systemType: updatedSearch.systemType || "all",
        });
        setState({
          ...state,
          systemConfigs: result.data,
          isDataLoaded: true,
        });
      } else {
        getDataCenters(result.data);
      }
    } else {
      showToast(
        result.message || "Error while getting System config values",
        "error"
      );
      setState({ ...state, isLoading: false });
    }
  };

  const getDataCenters = async (data: ISystemConfig[]) => {
    setState({ ...state, isLoading: true });
    let result = await getAllDataCenters();
    if (result?.success) {
      let datacenterDropdown: IDropdownOption[] = [];
      result.data.forEach((datacenter) => {
        datacenterDropdown.push({
          key: datacenter._id as string,
          text: datacenter.name,
        });
      });
      setState({
        ...state,
        systemConfigs: data,
        datacenterDropdown,
        isDataLoaded: true,
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(result.message || "Error while getting datacenters", "error");
    }
  };

  const handleDialogOpen = (isOpen: boolean, index?: number) => {
    let systemConfigs = undefined;
    if (index !== undefined) {
      systemConfigs = { ...state.systemConfigs[index] };
    }
    setState({
      ...state,
      manageSystemConfigDialog: {
        isOpen: true,
        index,
        data: systemConfigs,
      },
    });
  };

  const handleDialogClose = async (data: ISystemConfig) => {
    let systemConfigs = [...state.systemConfigs];
    if (data) {
      if (state.manageSystemConfigDialog.index !== undefined) {
        systemConfigs[state.manageSystemConfigDialog.index] = data;
        showToast("System config updated successfully", "success");
      } else {
        systemConfigs.unshift(data);
        showToast("System config added successfully", "success");
      }
    }
    setState({
      ...state,
      manageSystemConfigDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
      systemConfigs,
    });
  };

  const handleAddDatabaseDialogOpen = (isOpen: boolean, index?: number) => {
    let systemConfigs = undefined;
    if (index !== undefined) {
      systemConfigs = { ...state.systemConfigs[index] };
    }
    setState({
      ...state,
      manageDatabasesDialog: {
        isOpen: true,
        index,
        data: systemConfigs,
      },
    });
  };

  const handleAddDatabaseDialogClose = async (data: ISystemConfig) => {
    let systemConfigs = [...state.systemConfigs];
    if (data) {
      if (state.manageDatabasesDialog.index !== undefined) {
        systemConfigs[state.manageDatabasesDialog.index] = data;
        // showToast("Database configuration updated successfully", "success");
      }
      // else {
      //   systemConfigs.unshift(data);
      //   showToast("System config added successfully", "success");
      // }
    }
    setState({
      ...state,
      // manageDatabasesDialog: {
      //   isOpen: false,
      //   index: undefined,
      //   data: undefined,
      // },
      systemConfigs,
    });
  };

  const handleDatabaseTableClose = () => {
    setState({
      ...state,
      manageDatabasesDialog: {
        isOpen: false,
        index: undefined,
        data: undefined,
      },
    });
  };

  const deleteSc = async (item: ISystemConfig, index: number) => {
    showConfirmDialog(
      "Are You Sure",
      "Do you want to delete system config",
      async () => {
        setState({
          ...state,
          isLoading: true,
          manageActionsCallout: {
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
          },
        });
        let result = await deleteSystemConfig(item);
        if (result?.success) {
          showToast("System config deleted successfully", "success");
          let systemConfigs = [...state.systemConfigs];
          systemConfigs.splice(index, 1);
          setState({
            ...state,
            systemConfigs,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
        } else {
          setState({
            ...state,
            isLoading: false,
            manageActionsCallout: {
              isOpen: false,
              index: undefined,
              element: undefined,
              data: undefined,
            },
          });
          showToast(
            result.message || "Error while deleting system config",
            "error"
          );
        }
      }
    );
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column.key) {
      case "actions":
        return (
          <span
            onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
          >
            <Icon
              path={mdiDotsVertical}
              size={0.9}
              className="pointer"
              color={BRAND_SECONDARY}
            />
          </span>
          // <Stack horizontal>
          //   {item.systemType === "schneider" && item.connType === "mssql" ? (
          //     <Stack.Item>
          //       <TooltipHost
          //         content={"View Databases"}
          //         id={`viewDatabases`}
          //         calloutProps={{
          //           gapSpace: 0,
          //         }}
          //         styles={{
          //           root: {
          //             display: "inline-block",
          //           },
          //         }}
          //       >
          //         <span
          //           onClick={() => handleAddDatabaseDialogOpen(true, index)}
          //         >
          //           <Icon
          //             path={mdiDatabaseEdit}
          //             size={0.8}
          //             className="pointer"
          //             color={THEME.palette.neutralPrimary}
          //           />
          //         </span>
          //       </TooltipHost>
          //     </Stack.Item>
          //   ) : (
          //     <Stack.Item>
          //       <div style={{ width: 19.2 }}></div>
          //     </Stack.Item>
          //   )}
          //   &nbsp;&nbsp;
          //   <Stack.Item>
          //     <TooltipHost
          //       content={"Edit System Config"}
          //       id={`editSc`}
          //       calloutProps={{
          //         gapSpace: 0,
          //       }}
          //       styles={{
          //         root: {
          //           display: "inline-block",
          //         },
          //       }}
          //     >
          //       <span onClick={() => handleDialogOpen(true, index)}>
          //         <Icon
          //           path={mdiPencilOutline}
          //           size={0.8}
          //           className="pointer"
          //           color={THEME.palette.neutralPrimary}
          //         />
          //       </span>
          //     </TooltipHost>
          //   </Stack.Item>
          //   &nbsp;&nbsp;
          //   <Stack.Item>
          //     <TooltipHost
          //       content={"Delete System Config"}
          //       id={`deleteSc`}
          //       calloutProps={{ gapSpace: 0 }}
          //       styles={{ root: { display: "inline-block" } }}
          //     >
          //       <span onClick={() => deleteSc(item, index)}>
          //         <Icon
          //           path={mdiTrashCanOutline}
          //           size={0.8}
          //           className="pointer"
          //           color={THEME.palette.themePrimary}
          //         />
          //       </span>
          //     </TooltipHost>
          //   </Stack.Item>
          // </Stack>
        );
      case "systemType":
        return (
          <span style={{ fontWeight: 600 }}>
            {SC_SYSTEM_TYPE_DIC[item.systemType]}
          </span>
        );
      case "connType":
        return <span>{SC_CONNECTION_TYPE_DIC[item.connType]}</span>;
      case "datacenterId":
        if (
          item.systemType === "service-now" &&
          item.connType === "web-service"
        ) {
          return <span>Global</span>;
        } else {
          return (
            <span>
              {
                state.datacenterDropdown.find(
                  (d) => d.key === item?.datacenterId
                )?.text
              }
            </span>
          );
        }
      default:
        return (
          <span>
            {column.fieldName
              ? item[column.fieldName] !== ""
                ? item[column.fieldName]
                : "NA"
              : ""}
          </span>
        );
    }
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const handleLoading = () => {
    setState({
      ...state,
      isLoading: !state.isLoading,
    });
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: ISystemConfig
  ) => {
    setState({
      ...state,
      manageActionsCallout: {
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      },
    });
  };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap24}>
        <Stack.Item>
          <Stack horizontal horizontalAlign="space-between">
            <Stack.Item>
              {/* <Text variant="xLarge">System Configs</Text> */}
              <PageTitle
                icon={SystemConfigIcon}
                title="System Configs"
                description="List of all system configs"
              />
            </Stack.Item>
            {/* <Stack.Item>
              <PrimaryButton
                text="Add System Config"
                onClick={() => handleDialogOpen(true)}
                className="borderless-btn bg-white"
              />
            </Stack.Item> */}
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal horizontalAlign="space-between">
            <Stack.Item>
              <Stack horizontal wrap tokens={childrenGap}>
                <Stack.Item>
                  <SearchBox
                    value={searchObj.search}
                    styles={{ root: { width: "300px" } }}
                    placeholder="Search by server"
                    onChange={(e, value) =>
                      getSystemConfigs({ ...searchObj, search: value })
                    }
                  />
                </Stack.Item>
                {state.showDcFilter === false && (
                  <Stack.Item className="filter-btn">
                    <TooltipHost
                      content={"System Config Filters"}
                      id={`scdFilter`}
                      calloutProps={{ gapSpace: 0 }}
                      styles={{ root: { display: "inline-block" } }}
                    >
                      <span
                        onClick={() =>
                          setState({
                            ...state,
                            showDcFilter: true,
                          })
                        }
                      >
                        <Icon
                          path={mdiFilterOutline}
                          size={1.0}
                          className="pointer"
                          color={THEME.palette.neutralLighterAlt}
                        />
                      </span>
                    </TooltipHost>
                  </Stack.Item>
                )}
                {state.showDcFilter === true && (
                  <>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Select a system type"
                        selectedKey={searchObj.systemType}
                        styles={{ dropdown: { width: 250 } }}
                        options={[
                          { key: "all", text: "All" },
                          ...SC_SYSTEM_TYPE,
                        ]}
                        onChange={(e, v: IDropdownOption | undefined) => {
                          if (v) {
                            getSystemConfigs({
                              ...searchObj,
                              systemType: v.key as string,
                            });
                          }
                        }}
                      />
                    </Stack.Item>
                    <Stack.Item>
                      <Dropdown
                        placeholder="Select a datacenter"
                        selectedKey={searchObj.datacenterId}
                        styles={{ dropdown: { width: 250 } }}
                        options={[
                          { key: "all", text: "All" },
                          ...state.datacenterDropdown,
                        ]}
                        onChange={(e, v: IDropdownOption | undefined) => {
                          if (v) {
                            getSystemConfigs({
                              ...searchObj,
                              datacenterId: v.key as string,
                            });
                          }
                        }}
                      />
                    </Stack.Item>
                  </>
                )}
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <PrimaryButton
                text="Add System Config"
                onClick={() => handleDialogOpen(true)}
                className="borderless-btn bg-white"
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <div>
            {state.systemConfigs && state.systemConfigs.length > 0 ? (
              <Fabric>
                <DetailsList
                  items={state.systemConfigs}
                  columns={[
                    {
                      key: "systemType",
                      name: "System Type",
                      fieldName: "systemType",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                    {
                      key: "connType",
                      name: "Connection Type",
                      fieldName: "connType",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                    {
                      key: "server",
                      name: "Server",
                      fieldName: "server",
                      minWidth: 450,
                      maxWidth: 650,
                      isResizable: true,
                    },
                    // {
                    //   key: "instanceName",
                    //   name: "Instance Name",
                    //   fieldName: "instanceName",
                    //   minWidth: 200,
                    //   maxWidth: 300,
                    //   isResizable: true,
                    // },
                    {
                      key: "datacenterId",
                      name: "Datacenter Name",
                      fieldName: "datacenterId",
                      minWidth: 150,
                      maxWidth: 200,
                      isResizable: true,
                    },
                    {
                      key: "actions",
                      name: "Actions",
                      fieldName: "actions",
                      minWidth: 80,
                      maxWidth: 110,
                      isResizable: true,
                    },
                  ]}
                  compact={true}
                  setKey="set"
                  onRenderItemColumn={_renderItemColumn as any}
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                  onRenderRow={_onRenderRow}
                />
              </Fabric>
            ) : (
              <NoDataTile title="No System Config Found"></NoDataTile>
            )}
          </div>
        </Stack.Item>
      </Stack>
      {state.manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 170, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            state.manageActionsCallout && state.manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={state.manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
          // setInitialFocus={true}
        >
          <Stack tokens={childrenGap8}>
            {state.manageActionsCallout.data &&
              state.manageActionsCallout.data.systemType === "schneider" &&
              state.manageActionsCallout.data.connType === "mssql" && (
                <Stack.Item className="pointer">
                  <span
                    onClick={() => {
                      handleAddDatabaseDialogOpen(
                        true,
                        state.manageActionsCallout.index
                      );
                    }}
                    className="callout-options"
                  >
                    View Databases
                  </span>
                </Stack.Item>
              )}
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  handleDialogOpen(true, state.manageActionsCallout.index);
                }}
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  deleteSc(
                    state.manageActionsCallout.data as ISystemConfig,
                    state.manageActionsCallout.index as number
                  );
                }}
                className="callout-options"
              >
                Delete
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {state.manageSystemConfigDialog.isOpen && (
        <ManageSystemConfig
          data={state.manageSystemConfigDialog.data}
          handleLoading={handleLoading}
          handleDialogClose={(data: ISystemConfig) => {
            handleDialogClose(data);
          }}
          datacenterDropdown={state.datacenterDropdown}
        />
      )}
      {/* {state.manageDatabasesDialog.isOpen && (
        <ManageDatabaseDialog
          data={state.manageDatabasesDialog.data}
          handleLoading={handleLoading}
          handleDialogClose={(data: ISystemConfig) => {
            handleAddDatabaseDialogClose(data);
          }}
        />
      )} */}
      {state.manageDatabasesDialog.isOpen && (
        <ManageDatabaseDialogTable
          data={state.manageDatabasesDialog.data}
          handleDialogClose={(data: ISystemConfig) => {
            handleAddDatabaseDialogClose(data);
          }}
          handleDatabaseTableClose={handleDatabaseTableClose}
        />
      )}
    </React.Fragment>
  );
};

export default SystemConfigMain;
